'use client'

import { useTranslation } from 'react-i18next'

import { useParams, useRouter, useSearchParams } from 'next/navigation'

import { ButtonClick, track } from '@dfds-frontend/tracking'
import { Button } from '@dfds-pax/shared-components/client'

import { useBasket } from '@/providers/basket-context'
import EditIcon from '@/ui-cvi/components/dxp-icons/system/EditIcon'

import { getSessionBasket } from '@/utils/sessionStorage'

import { Step } from './steps'

export const NavigateButton = ({ isVertical, onClick, step }: { isVertical: boolean; onClick?: () => void; step: Step }) => {
  const router = useRouter()
  const params = useParams() as { ferry: string; locale: string }
  const searchParams = useSearchParams()
  const { basketId: contextBasketId } = useBasket()
  // useSearchParams().get() can return null; fall back to the basket context (session-backed)
  // so we never build a `?basketId=null` URL when navigating between steps.
  const basketId = searchParams.get('basketId') ?? contextBasketId
  const { t } = useTranslation()

  const handleClick = () => {
    if (onClick) {
      onClick()
    }
    if (step.stepType !== 'CONFIRMATION') {
      let placeToNavigate = undefined
      if (step.stepType === 'DEPARTURES') placeToNavigate = 'yourbooking'
      else if (step.stepType === 'PETS') placeToNavigate = 'pets'
      else if (step.stepType === 'EXTRAS') placeToNavigate = 'extras'
      else if (step.stepType === 'PASSENGERS') placeToNavigate = 'passengers'
      else if (step.stepType === 'CHECKOUT') placeToNavigate = 'checkout'
      else if (step.stepType === 'REVIEW') placeToNavigate = 'review'

      track<ButtonClick>({
        clickedElementText: `${step.stepType}`,
        event: 'buttonClick',
        eventAction: isVertical ? 'StepVerticalClicked' : 'StepHorizontalClicked',
      })

      if (placeToNavigate) {
        // Prefer the URL/context id, but fall back to sessionStorage so we never navigate to a
        // bare step URL (which the middleware rejects and bounces to marketing) when the basket
        // context has not populated yet.
        let sessionBasketId = ''
        try {
          sessionBasketId = getSessionBasket()?.basketId ?? ''
        } catch {
          // Ignore sessionStorage read/parse errors and fall back to the default empty id.
        }
        const resolvedBasketId = basketId || sessionBasketId
        const basketIdQuery = resolvedBasketId ? `?basketId=${encodeURIComponent(resolvedBasketId)}` : ''
        router.push(`/${params?.locale}/${params?.ferry}/${placeToNavigate}${basketIdQuery}`)
      }
    }
  }

  return (
    <Button variant="ghostPrimary" data-e2e={`edit-step-${step.stepType.toLowerCase()}`} onClick={handleClick} type="button">
      {t('EDIT')} <EditIcon size="md" />
    </Button>
  )
}
